(() => {
  const mobile = matchMedia('(max-width: 760px)');
  if (!mobile.matches) return;

  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const center = (button) => {
    if (!button) return;
    requestAnimationFrame(() => button.scrollIntoView({
      behavior: reduced.matches ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center',
    }));
  };

  const setCurrent = (buttons, current) => {
    buttons.forEach((button) => {
      const active = button === current;
      button.classList.toggle('is-current', active);
      button.setAttribute('aria-selected', String(active));
      button.tabIndex = active ? 0 : -1;
    });
  };

  const install = ({ root, surface, buttonSelector, activeSelector }) => {
    if (!root || !surface || root.dataset.tcMobileSwipe === 'true') return;
    const buttons = [...root.querySelectorAll(buttonSelector)];
    if (buttons.length < 2) return;
    root.dataset.tcMobileSwipe = 'true';
    let startX = 0;
    let startY = 0;
    let tracking = false;

    const activeIndex = () => Math.max(0, buttons.findIndex((button) => button.matches(activeSelector)));
    buttons.forEach((button) => button.addEventListener('click', () => center(button)));

    surface.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' || event.target.closest('button, a, video, iframe, input, summary')) return;
      startX = event.clientX;
      startY = event.clientY;
      tracking = true;
    }, { passive: true });

    surface.addEventListener('pointerup', (event) => {
      if (!tracking) return;
      tracking = false;
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;
      if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.25) return;
      const current = activeIndex();
      const next = deltaX < 0
        ? Math.min(buttons.length - 1, current + 1)
        : Math.max(0, current - 1);
      if (next === current) return;
      buttons[next].click();
      center(buttons[next]);
    }, { passive: true });

    surface.addEventListener('pointercancel', () => { tracking = false; }, { passive: true });
    center(buttons[activeIndex()]);
  };

  const testing = document.querySelector('[data-testing-proof]');
  const testingIndex = testing?.querySelector('.tc-tv-index');
  const testingPlayer = testing?.querySelector('[data-tv-player]');
  const testingVideo = testing?.querySelector('[data-tv-video]');
  if (testingPlayer && testingVideo && !document.querySelector('[data-tc-mobile-video-modal]')) {
    testingVideo.controls = false;
    testingVideo.removeAttribute('controls');
    testingVideo.setAttribute('playsinline', '');
    testingVideo.setAttribute('webkit-playsinline', '');

    const modal = document.createElement('div');
    modal.className = 'tc-mobile-video-modal';
    modal.dataset.tcMobileVideoModal = '';
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'TOCCAY engineering test video');
    modal.innerHTML = '<div class="tc-mobile-video-dialog"><video class="tc-mobile-video-media" controls playsinline webkit-playsinline preload="metadata" tabindex="0"></video><button class="tc-mobile-video-close" type="button" aria-label="Close video">×</button></div>';
    document.body.append(modal);

    const modalVideo = modal.querySelector('.tc-mobile-video-media');
    const modalClose = modal.querySelector('.tc-mobile-video-close');
    const originalClose = testing.querySelector('[data-tv-close]');
    let trigger = null;
    let scrollY = 0;

    const closeModal = () => {
      if (modal.hidden) return;
      modalVideo.pause();
      modalVideo.removeAttribute('src');
      modalVideo.load();
      modal.hidden = true;
      document.documentElement.classList.remove('tc-mobile-video-open');
      document.body.classList.remove('tc-mobile-video-open');
      document.body.style.removeProperty('top');
      window.scrollTo(0, scrollY);
      trigger?.focus({ preventScroll: true });
    };
    const openModal = (src, button) => {
      if (!src) return;
      trigger = button;
      scrollY = window.scrollY;
      modal.hidden = false;
      document.documentElement.classList.add('tc-mobile-video-open');
      document.body.classList.add('tc-mobile-video-open');
      document.body.style.top = `-${scrollY}px`;
      modalVideo.src = src;
      modalVideo.load();
      modalVideo.play().catch(() => {});
      requestAnimationFrame(() => modalClose.focus({ preventScroll: true }));
    };

    testing.querySelectorAll('[data-tv-play]').forEach((button) => {
      button.addEventListener('click', () => {
        const src = testingVideo.currentSrc || testingVideo.getAttribute('src') || '';
        if (originalClose) originalClose.click();
        else {
          testingVideo.pause();
          testing.classList.remove('is-playing');
        }
        openModal(src, button);
      });
    });
    modalClose.addEventListener('click', closeModal);
    modal.addEventListener('click', (event) => { if (event.target === modal) closeModal(); });
    modalVideo.addEventListener('ended', closeModal);
    document.addEventListener('keydown', (event) => {
      if (modal.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusables = [modalVideo, modalClose];
      const current = focusables.indexOf(document.activeElement);
      const next = event.shiftKey
        ? (current <= 0 ? focusables.length - 1 : current - 1)
        : (current >= focusables.length - 1 ? 0 : current + 1);
      event.preventDefault();
      focusables[next].focus({ preventScroll: true });
    });
  }
  if (testingIndex && !testing?.querySelector('[data-tc-test-summary]')) {
    const summary = document.createElement('div');
    summary.className = 'tc-mobile-current-summary';
    summary.dataset.tcTestSummary = '';
    testingIndex.insertAdjacentElement('afterend', summary);
    const syncTestSummary = () => {
      const active = testing.querySelector('.tc-tv-chapter.is-active .tc-tv-panel');
      const label = active?.querySelector('.tc-tv-panel-label')?.textContent?.trim() || '';
      const copy = active?.querySelector('.tc-tv-panel-copy')?.textContent?.trim() || '';
      summary.innerHTML = `<small>${label}</small><p>${copy}</p>`;
    };
    testing.querySelectorAll('[data-tv-tab]').forEach((button) => button.addEventListener('click', () => requestAnimationFrame(syncTestSummary)));
    syncTestSummary();
  }
  install({
    root: testing,
    surface: testingPlayer,
    buttonSelector: '[data-tv-tab]',
    activeSelector: '[aria-selected="true"]',
  });

  const philosophy = document.querySelector('[data-tc-philosophy]');
  const philosophyVisual = philosophy?.querySelector('.tc-phil-visual');
  const philosophyRail = philosophy?.querySelector('.tc-phil-rail');
  if (philosophyVisual && philosophyRail && !philosophy?.querySelector('[data-tc-phil-mobile-nav]')) {
    const nav = document.createElement('div');
    nav.className = 'tc-phil-mobile-nav';
    nav.dataset.tcPhilMobileNav = '';
    nav.setAttribute('role', 'tablist');
    nav.setAttribute('aria-label', 'Why TOCCAY');
    const originals = [...philosophyRail.querySelectorAll('[data-phil-word]')];
    originals.forEach((original, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.innerHTML = `<span>${String(index + 1).padStart(2, '0')}</span><b>${original.querySelector('strong')?.textContent?.trim() || ''}</b>`;
      button.addEventListener('click', () => {
        original.click();
        setCurrent([...nav.querySelectorAll('button')], button);
        center(button);
      });
      original.addEventListener('click', () => {
        setCurrent([...nav.querySelectorAll('button')], button);
        center(button);
      });
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', String(index === 0));
      button.tabIndex = index === 0 ? 0 : -1;
      if (index === 0) button.classList.add('is-current');
      nav.append(button);
    });
    philosophyVisual.insertAdjacentElement('afterend', nav);
  }
  install({
    root: philosophy,
    surface: philosophyVisual,
    buttonSelector: '[data-phil-word]',
    activeSelector: '.is-current',
  });
})();
